import {toast} from '@wandb/components';
import React, {Suspense, useContext, useRef} from 'react';

import {selectHasRunningThread} from '../../agentChat/store';
import {useStoreSelector} from '../../agentChat/store/context';
import {useUnseenSettledThread} from '../../agentChat/useUnseenSettledThread';
import {trackNavRightFeatureOpened} from '../../services/analytics/productEvents';
import {
  useIsAdminModeActiveAndIsNotLocalEnv,
  viewingAs,
} from '../../util/admin';
import {createPreloadableLazyComponent} from '../../util/lazyImport';
import {
  AgentChatProvider,
  useAgentChatAvailability,
  useWBAgentController,
} from '../AgentChat';
import {AgentChatRuntimeBoundary} from '../AgentChat/AgentChatProvider';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {closeCustomizationSection} from './customizations/useCustomizationsStore';
import {useTrackWBAgentPanel} from './useTrackWBAgent';
import {useWBAgentIntroTooltip} from './useWBAgentIntroTooltip';
import {WBAgentChatButton} from './WBAgentChatButton';
import {WBAgentSessionRestore} from './WBAgentSessionRestore';
import {useWBAgentWindowStore} from './window/useWBAgentWindowStore';

const wBAgentOpenPanel = createPreloadableLazyComponent(
  () => import('./WBAgentOpenPanel'),
  module => module.WBAgentOpenPanel
);
const LazyWBAgentOpenPanel = wBAgentOpenPanel.Component;
export const preloadWBAgentOpenPanel = wBAgentOpenPanel.preload;

function preloadWBAgentOpenPanelOnIntent(): void {
  void preloadWBAgentOpenPanel().catch(() => undefined);
}

/**
 * Component tree:
 * WBAgent → WBAgentInner
 *   ├── WBAgentChatButton
 *   ├── WBAgentChatWindow (floating, undocked)
 *   │   ├── WBAgentHeader
 *   │   ├── WBAgentChatHistorySidebar
 *   │   │   └── WBAgentCustomizations
 *   │   └── WBAgentPanelContent
 *   │       ├── WBAgentThread
 *   │       │   ├── WBAgentMessageAssistant
 *   │       │   ├── WBAgentMessageUser
 *   │       │   └── WBAgentComposer
 *   │       │       └── WBAgentEditor
 *   │       └── ProjectMemoryView
 *   └── WBAgentChatDrawer (docked)
 *       ├── WBAgentHeader
 *       └── WBAgentPanelContent
 *           └── ...
 */

export const WBAgent = () => {
  return (
    <AgentChatProvider>
      <WBAgentInner />
    </AgentChatProvider>
  );
};

export const WBAgentInner = () => {
  const {isAvailable: isWorkspaceAgentEnabled} = useAgentChatAvailability();
  const hasRunningThread = useStoreSelector(selectHasRunningThread);
  const open = useWBAgentWindowStore(s => s.open);
  const setOpen = useWBAgentWindowStore(s => s.setOpen);
  const docked = useWBAgentWindowStore(s => s.docked);
  const {selectedAccount} = useContext(AccountSelectorContext);
  const isAdminMode =
    useIsAdminModeActiveAndIsNotLocalEnv() || viewingAs() !== '';
  const {show: showIntroTooltip, hide: hideIntroTooltip} =
    useWBAgentIntroTooltip(isWorkspaceAgentEnabled);
  const {trackOpened, trackClosed, trackNavbarButtonViewed} =
    useTrackWBAgentPanel();
  const {openChatToThread} = useWBAgentController();

  const activeToastRef = useRef<{threadId: string; key: string} | null>(null);
  // Handles notifying user via toast that a chat completed while the agent was minimized.
  // The callback only fires for live completions --> the toast won't re-appear
  // when the persisted value is rehydrated after a page reload.
  const unseenSettledThread = useUnseenSettledThread(
    open,
    thread => {
      // Only ever show toast for the most recently completed thread
      const prev = activeToastRef.current;
      if (prev != null) {
        toast.dismiss(prev.key);
      }
      const key = toast.success('New response', {
        action: {
          label: 'Go to chat',
          onPress: () => {
            preloadWBAgentOpenPanelOnIntent();
            toast.dismiss(key);
            openChatToThread(thread.threadId);
          },
        },
      });
      activeToastRef.current = {threadId: thread.threadId, key};
    },
    thread => {
      // onSeen func for dismissing toast
      const active = activeToastRef.current;
      if (active != null && active.threadId === thread.threadId) {
        toast.dismiss(active.key);
        activeToastRef.current = null;
      }
    }
  );

  return (
    <>
      {isWorkspaceAgentEnabled && (
        <WBAgentChatButton
          showIntroTooltip={showIntroTooltip}
          hideIntroTooltip={hideIntroTooltip}
          unseenSettledThread={unseenSettledThread}
          onImpression={trackNavbarButtonViewed}
          onPress={() => {
            preloadWBAgentOpenPanelOnIntent();
            if (!open) {
              trackNavRightFeatureOpened({
                feature: 'agent',
                selected_account: selectedAccount,
                is_admin_mode: isAdminMode,
              });
              trackOpened();
              closeCustomizationSection();
            } else {
              trackClosed();
            }
            hideIntroTooltip();
            setOpen(!open);
          }}
          onHoverStart={preloadWBAgentOpenPanelOnIntent}
          onFocus={preloadWBAgentOpenPanelOnIntent}
          onPressStart={preloadWBAgentOpenPanelOnIntent}
          isLoading={hasRunningThread}
          aria-expanded={open}
        />
      )}
      <AgentChatRuntimeBoundary>
        <WBAgentSessionRestore onRestore={preloadWBAgentOpenPanelOnIntent} />
        {open && (
          <Suspense fallback={null}>
            <LazyWBAgentOpenPanel docked={docked} />
          </Suspense>
        )}
      </AgentChatRuntimeBoundary>
    </>
  );
};
